import { Card, CardHeading, FormattedDateTime } from "@dgs/core";
import { FC, useId } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GuestTravelFormValues } from "../../../shared/types/travel";
import { isTravelSegmentIndexResource } from "../../../shared/types/travelSegment";
import { BaseCard } from "./drawer/SelectableTravelSegmentBaseCard";
import { CardBody, Strong, TravelCard } from "./segmentCard/Card";
import { LabeledValue } from "./segmentCard/LabeledValue";
import { TravelInquirySummaryCard } from "./segmentCard/TravelInquirySummaryCard";
import { TravelSegmentSummaryCard } from "./segmentCard/TravelSegmentSummaryCard";

interface Props {
	travels: GuestTravelFormValues[];
}

interface TraveListItemProps {
	travel: GuestTravelFormValues;
	heading: string;
}

const ArrivalDepartureCard = styled(Card).attrs({ $variant: "outlined" as const })`
	display: grid;
	background: ${({ theme }) => theme.colors.palette.grey[10]};
	grid-template:
		"arrivalHeader" max-content
		"arrivalList" 1fr
		"departureHeader" max-content
		"departureList" 1fr / 1fr;

	${({ theme }) => theme.breakpoints.largeDesktop} {
		grid-template:
			"arrivalHeader departureHeader" max-content
			"arrivalList departureList" 1fr / 1fr 1fr;
	}
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(6)};
`;

const Remarks = styled(BaseCard)`
	display: flex;
	flex-direction: column;

	background-color: ${({ theme }) => theme.colors.palette.grey[30]};
`;

const RemarksHeader = styled.div`
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
	padding-bottom: ${({ theme }) => theme.spacing(2)};
`;

const ArrivalRemarks = styled(Remarks)`
	grid-area: arrivalRemarks;
`;

const DepartureRemarks = styled(Remarks)`
	grid-area: departureRemarks;
`;

const ArrivalHeader = styled(Header)`
	grid-area: arrivalHeader;

	${({ theme }) => theme.breakpoints.largeDesktop} {
		border-right: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	}
`;

const DepartureHeader = styled(Header)`
	grid-area: departureHeader;
`;

const List = styled.ul`
	display: flex;
	flex-direction: column;
	list-style: none;
	gap: ${({ theme }) => theme.spacing(2)};
	padding: ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(6)};
`;

const ArrivalList = styled(List)`
	grid-area: arrivalList;

	${({ theme }) => theme.breakpoints.largeDesktop} {
		border-right: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	}
`;

const DepartureList = styled(List)`
	grid-area: departureList;
`;

const TravelHeading = styled.h2`
	color: ${({ theme }) => theme.colors.palette.grey[90]};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
	font-size: ${({ theme }) => theme.fonts.sizes.large};
`;

const TravelLisItem: FC<TraveListItemProps> = ({ heading, travel }) => {
	const { t } = useTranslation();
	const headingId = useId();

	return (
		<section aria-labelledby={headingId}>
			<TravelHeading id={headingId}>{heading}</TravelHeading>
			<ArrivalDepartureCard>
				<ArrivalHeader>
					<CardHeading>{t("Arrival")}</CardHeading>
				</ArrivalHeader>
				<ArrivalList aria-label={t("Arrival")}>
					{travel.arrivalInquiry && (
						<li aria-label={`${t("Inquiry")}: ${t(travel.arrivalInquiry.type)}`}>
							<TravelInquirySummaryCard travelInquiry={travel.arrivalInquiry} />
						</li>
					)}
					{travel.arrivalTravelTemplate?.travelSegments.map((travelSegment) => (
						<li key={travelSegment.id}>
							<TravelSegmentSummaryCard travelSegment={travelSegment} direction="arrival" />
						</li>
					))}
					{travel.arrivals.map((travelSegment, index) => (
						<li key={isTravelSegmentIndexResource(travelSegment) ? travelSegment.id : `form-values ${index}`}>
							<TravelSegmentSummaryCard travelSegment={travelSegment} direction="arrival" />
						</li>
					))}
					{travel.arrivalSelfTravel && (
						<li aria-label={t("Self travel")}>
							<TravelCard>
								<div>
									<Strong>{t("Self travel")}</Strong>
								</div>
								<CardBody>
									<FormattedDateTime dateTime={`${travel.arrivalSelfTravel.date}T${travel.arrivalSelfTravel.time}`} />
								</CardBody>
								{travel.arrivalSelfTravel.remarks && (
									<LabeledValue label={t("Remarks")}>{travel.arrivalSelfTravel.remarks}</LabeledValue>
								)}
							</TravelCard>
						</li>
					)}
					{travel.arrivalRemarks && (
						<ArrivalRemarks>
							<RemarksHeader>{t("Remarks")}:</RemarksHeader>
							{travel.arrivalRemarks}
						</ArrivalRemarks>
					)}
				</ArrivalList>
				<DepartureHeader>
					<CardHeading>{t("Departure")}</CardHeading>
				</DepartureHeader>
				<DepartureList aria-label={t("Departure")}>
					{travel.departureInquiry && (
						<li aria-label={`${t("Inquiry")}: ${t(travel.departureInquiry.type)}`}>
							<TravelInquirySummaryCard travelInquiry={travel.departureInquiry} />
						</li>
					)}
					{travel.departureTravelTemplate?.travelSegments.map((travelSegment) => (
						<li key={travelSegment.id}>
							<TravelSegmentSummaryCard travelSegment={travelSegment} direction="departure" />
						</li>
					))}
					{travel.departures.map((travelSegment, index) => (
						<li key={isTravelSegmentIndexResource(travelSegment) ? travelSegment.id : `form-values ${index}`}>
							<TravelSegmentSummaryCard travelSegment={travelSegment} direction="departure" />
						</li>
					))}
					{travel.departureSelfTravel && (
						<li aria-label={t("Self travel")}>
							<TravelCard>
								<div>
									<Strong>{t("Self travel")}</Strong>
								</div>
								<CardBody>
									<FormattedDateTime
										dateTime={`${travel.departureSelfTravel.date}T${travel.departureSelfTravel.time}`}
									/>
									{travel.departureSelfTravel.remarks && (
										<LabeledValue label={t("Remarks")}>{travel.departureSelfTravel.remarks}</LabeledValue>
									)}
								</CardBody>
							</TravelCard>
						</li>
					)}
					{travel.departureRemarks && (
						<DepartureRemarks>
							<RemarksHeader>{t("Remarks")}:</RemarksHeader>
							{travel.departureRemarks}
						</DepartureRemarks>
					)}
				</DepartureList>
			</ArrivalDepartureCard>
		</section>
	);
};

const TraveList = styled.ul`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(6)};
	list-style: none;
`;

export const TravelSummary: FC<Props> = ({ travels }) => {
	const { t } = useTranslation();

	return (
		<TraveList>
			{travels.map((travel, index) => (
				<li key={index}>
					<TravelLisItem travel={travel} heading={t("{{number}}. Travel", { number: index + 1 })} />
				</li>
			))}
		</TraveList>
	);
};
